import { createContext, useState, useEffect } from "react";
import { auth, db, storage } from "../services/firebase";

import {
  query,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setCurrentUser(user);
        fetchUserData(user.uid);
      } else {
        setCurrentUser(null);
        setProfile(null);
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setCurrentUser(credential.user);
      fetchUserData(credential.user.uid);
      try {
        return credential;
      } catch (err) {
        console.error("Error fetching data:", err);
        throw err;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const fetchUserData = async (userId) => {
    try {
      const userDocRef = doc(db, "accounts", userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setProfile(userData);
      } else {
        console.warn("No data found for user:", userId);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const createAccount = async (email, password, firstName, lastName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;
      const uid = user.uid;

      const accountsRef = collection(db, "accounts");
      const userDocRef = doc(accountsRef, uid);

      await setDoc(userDocRef, {
        uid: uid,
        firstName: firstName,
        lastName: lastName,
        email: email,
      });
    
      await sendEmailVerification(user);

      return userCredential;
    } catch (error) {
      console.error("Account creation error:", error);
      return null;
    }
  };

  const addWaitlistUser = async (email, name, number, company) => {
    const waitlistRef = collection(db, "waitlist");
    const q = query(waitlistRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      const newDocRef = doc(waitlistRef);
      await setDoc(newDocRef, {
        name: name,
        email: email,
        number: number,
        company: company,
      });

      const [firstName, lastName] = name.split(" ");
      // await addToSendGrid(email, firstName, lastName);
    }
  };

  const addUserToNewsletter = async (email) => {
    const waitlistRef = collection(db, "waitlist");

    const newDocRef = doc(waitlistRef);

    return setDoc(newDocRef, {
      email: email,
    });
  };

  const updateAccountProfilePicture = async (userId, file) => {
    try {
      const storageRef = ref(storage, `profile_pictures/${userId}`);

      await uploadBytes(storageRef, file);

      // Get the download URL directly
      const downloadURL = await getDownloadURL(storageRef);

      const userDocRef = doc(db, "accounts", userId);
      await updateDoc(userDocRef, {
        profilePicture: downloadURL,
      });

      setProfile((prevProfile) => ({
        ...prevProfile,
        profilePicture: downloadURL,
      }));

    } catch (error) {
      console.error("Error updating profile picture:", error);
    }
  };

  const addContactSubmission = async (name, email, company, message) => {
    const contactsRef = collection(db, "contacts");
    const newDocRef = doc(contactsRef);
    await setDoc(newDocRef, {
      name,
      email,
      company,
      message,
      createdAt: new Date().toISOString(),
    });
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await auth.signOut();
      setCurrentUser(null);
      setProfile(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        signIn,
        profile,
        createAccount,
        error,
        addWaitlistUser,
        addUserToNewsletter,
        addContactSubmission,
        currentUser,
        authLoading,
        updateAccountProfilePicture,
        resetPassword,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};